import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useLocation, useNavigate } from "react-router-dom";
import Card from "@mui/material/Card";
import SoftBox from "components/SoftBox";
import SearchField from "components/SearchField";
import SoftTypography from "components/SoftTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "examples/Tables/Table";
import SoftAvatar from "components/SoftAvatar";
import SoftBadge from "components/SoftBadge";
import userImage from "assets/images/illustrations/user.png";
import SoftButton from "components/SoftButton";
import { ApiResponse } from "model/apiResponseModel";
import { ProjectModel } from "model/projectListModel";
import { convertSecondsToHoursLive } from "helpers/logMessages";
import api from "api/api";

function ProjectsList() {
  const [columns, setColumns] = useState([]);
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [totalPages, setTotalPages] = useState(1);
  const limit = 10;
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const userId = queryParams.get("userId");

  const handleSearch = () => {
    if (searchQuery != "") {
      fetchProjects(1, searchQuery);
    }
  };

  const handleClearSearch = () => {
    if (searchQuery != "") {
      setSearchQuery("");
      fetchProjects(1, "");
      // console.log("Multiple functions are executed after a delay.");
    }
  };
  // useEffect(() => {

  //   fetchProjects(1, searchQuery);
  // }, [searchQuery]);

  const fetchProjects = async (page = 1, search) => {
    try {
      setIsLoading(true);
      let postData = {
        limit,
        page,
      };

      if (userId && userId !== "") {
        postData.userId = userId;
      }
      if (search && search.trim() !== "") {
        postData.search = search.trim();
      }

      const response = await api.getProjectsList(postData);
      const apiResponse = new ApiResponse(response.status, response.message);

      if (apiResponse.status === 1) {
        const projectModelData = new ProjectModel(response.data);
        // console.log(projectModelData);
        setColumns([
          { name: "project", align: "left" },
          { name: "design", align: "center" },
          { name: "artist", align: "center" },
          { name: "brand", align: "center" },
          { name: "logged Time", align: "center" },
          { name: "created", align: "center" },
          { name: "status", align: "center" },
        ]);
        setRows(
          projectModelData.projects.map((project) => ({
            linkPath: `/project-detail?projectId=${project._id}`,
            project: (
              <SoftBox display="flex" alignItems="center" px={1} pb={0.5}>
                <SoftAvatar
                  src={project.image[0] || userImage}
                  alt={project.name}
                  size="sm"
                  variant="rounded"
                />
                <SoftBox display="flex" flexDirection="column" ml={2}>
                  <SoftTypography variant="button" fontWeight="medium">
                    {project.name}
                  </SoftTypography>
                  <SoftTypography variant="caption" color="secondary">
                    {project.email}
                  </SoftTypography>
                </SoftBox>
              </SoftBox>
            ),
            design: (
              <SoftTypography
                variant="caption"
                color="secondary"
                fontWeight="medium"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  height: "100%",
                  minHeight: "40px",
                }}
              >
                {project.design}
              </SoftTypography>
            ),
            artist: (
              <SoftTypography variant="caption" color="secondary" fontWeight="medium">
                {project.artist}
              </SoftTypography>
            ),
            brand: (
              <SoftTypography variant="caption" color="secondary" fontWeight="medium">
                {project.brand}
              </SoftTypography>
            ),
            "logged Time": (
              <SoftTypography variant="caption" color="secondary" fontWeight="medium">
                {convertSecondsToHoursLive(project.totalDuration)}
              </SoftTypography>
            ),
            created: (
              <SoftTypography variant="caption" color="secondary" fontWeight="medium">
                {new Date(project.created_at * 1000).toLocaleDateString("en-US", {
                  month: "2-digit",
                  day: "2-digit",
                  year: "numeric",
                })}
              </SoftTypography>
            ),
            status: (
              <SoftBadge
                variant="gradient"
                badgeContent={
                  project.project_status === "not_started"
                    ? "Not Started"
                    : project.project_status === "ongoing"
                    ? "Ongoing"
                    : "Completed"
                }
                color={
                  project.project_status === "not_started"
                    ? "error"
                    : project.project_status === "ongoing"
                    ? "warning"
                    : "success"
                }
                size="xs"
              />
            ),
          }))
        );
        setTotalPages(response.data.totalPages);
      } else {
        alert(apiResponse.message);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const navigate = useNavigate();
  useEffect(() => {
    const loginData = JSON.parse(
      localStorage.getItem("loginData") || sessionStorage.getItem("loginData")
    );

    // Check if the user is logged in
    if (!loginData) {
      navigate("/authentication/sign-in");
    } else {
      // If logged in, fetch users for the current page
      fetchProjects(currentPage, "");
    }
  }, [currentPage, navigate]);

  const handlePageClick = (page) => {
    // if (page >= 1 && page <= totalPages) {
    setCurrentPage(page);
    // console.log(page);
    fetchProjects(page, "");
    // }
  };

  // const handlePageChange = (event, page) => {
  //   setCurrentPage(page);
  //   fetchUsers(currentPage);
  // };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>
        <SoftBox mb={3}>
          <Card>
            <SoftBox display="flex" justifyContent="space-between" alignItems="center" p={3}>
              <SoftTypography variant="h6">Projects Table</SoftTypography>
              <div>
                <SearchField
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onSearch={handleSearch}
                  onClear={handleClearSearch}
                />
              </div>
            </SoftBox>
            <SoftBox
              sx={{
                "& .MuiTableRow-root:not(:last-child)": {
                  "& td": {
                    borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                      `${borderWidth[1]} solid ${borderColor}`,
                  },
                },
              }}
            >
              {isLoading ? (
                <SoftBox
                  display="flex"
                  height="500px"
                  justifyContent="center"
                  alignItems="center"
                  p={3}
                >
                  <SoftTypography variant="subtitle1" color="textSecondary">
                    Loading...
                  </SoftTypography>
                </SoftBox>
              ) : rows.length > 0 ? (
                <Table columns={columns} rows={rows} />
              ) : (
                <SoftBox display="flex" justifyContent="center" alignItems="center" p={3}>
                  <SoftTypography variant="subtitle1" color="textSecondary">
                    No Projects Available
                  </SoftTypography>
                </SoftBox>
              )}
            </SoftBox>
            <SoftBox display="flex" alignItems="center" justifyContent="center" py={2}>
              {/* Previous Button */}
              <SoftButton
                variant="outlined"
                color="primary"
                onClick={() => handlePageClick(currentPage - 1)}
                disabled={currentPage === 1}
                sx={{ mx: 1 }}
              >
                Previous
              </SoftButton>

              {/* Page Numbers */}
              {/* {[...Array(totalPages)].map((_, index) => {
        const pageNumber = index + 1;
        return ( */}
              <SoftButton
                key={currentPage}
                variant="contained"
                color="primary"
                // onClick={() => handlePageClick(pageNumber)}
                sx={{ mx: 0.5 }}
              >
                {currentPage}
              </SoftButton>
              {/* );
      })} */}

              {/* Next Button */}
              <SoftButton
                variant="outlined"
                color="primary"
                onClick={() => handlePageClick(currentPage + 1)}
                disabled={currentPage === totalPages}
                sx={{ mx: 1 }}
              >
                Next
              </SoftButton>
            </SoftBox>
          </Card>
        </SoftBox>
      </SoftBox>
      <Footer />
    </DashboardLayout>
  );
}

export default ProjectsList;
